import { useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useMyPreferences, useSaveLanguagePreference } from '../api/preferences';
import { applyUiLanguage, useTranslation } from './index';
import {
  choiceStorageKey,
  normalizeLanguage,
  readStored,
  userKeyFromAccount,
  writeStored,
  type UiLanguage,
} from './language';

/**
 * Set once the person uses the toggle in this page's life. From then on the toggle owns the
 * language and a server copy that arrives late (a slow first read) must never undo their click —
 * ptc-statetax #54's "settled" rule.
 */
let chosenThisSession = false;

/** For tests: forget that a choice was made in this page's life. */
export function resetLanguageSessionForTests(): void {
  chosenThisSession = false;
}

/** The language the UI is in now, and the switch that records the person's own choice. */
export function useUiLanguage(): { language: UiLanguage; setLanguage: (lang: UiLanguage) => void } {
  const { i18n } = useTranslation();
  const { accounts } = useMsal();
  const userKey = userKeyFromAccount(accounts[0]);
  const { mutate: save } = useSaveLanguagePreference();
  const language = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language) ?? 'en';

  const setLanguage = useCallback(
    (lang: UiLanguage) => {
      chosenThisSession = true;
      writeStored(choiceStorageKey(userKey), lang);
      applyUiLanguage(lang);
      // Follows the person to another computer. A failed save leaves this browser's choice in place.
      if (userKey) save(lang);
    },
    [userKey, save],
  );
  return { language, setLanguage };
}

/**
 * Applies the signed-in person's language. Renders nothing.
 *   - At once: their locally cached choice (so the first paint is already right).
 *   - When `/my-preferences` arrives: their server-side choice, ONCE, unless they toggled meanwhile —
 *     and it refreshes the local cache, so the next paint on this computer is right too.
 * Nothing here decides a language for anyone: with no choice anywhere, the browser's language (from
 * the i18n init) simply stands.
 */
export function UiLanguageBootstrap() {
  const { accounts } = useMsal();
  const userKey = userKeyFromAccount(accounts[0]);
  const cached = userKey ? readStored(choiceStorageKey(userKey)) : null;
  const prefs = useMyPreferences(!!userKey);

  useEffect(() => {
    if (cached && !chosenThisSession) applyUiLanguage(cached);
  }, [cached]);

  useEffect(() => {
    const saved = normalizeLanguage(prefs.data?.language);
    if (!saved || chosenThisSession || !userKey) return;
    writeStored(choiceStorageKey(userKey), saved);
    applyUiLanguage(saved);
  }, [prefs.data, userKey]);

  return null;
}
